import React, { useCallback, useEffect, useState } from 'react';
import { useLazyGetVoicesListQuery, useSetUserVoiceMutation, useLazyGetUserVoiceQuery } from '../../api/speech.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import IconPause from '../../components/images/IconPause.jsx';
import { Dialog } from 'primereact/dialog';
import { debounce } from 'lodash';
import IconCloseFull from '../../components/images/IconCloseFull.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import IconPlay from '../../components/images/IconPlay.jsx';
import { getInitials } from '../../utils/getInitials.js';

const Speechify = ({ visible, setVisible }) => {
    const [append, setAppend] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [currentAudio, setCurrentAudio] = useState(null);
    const [currentPlayingId, setCurrentPlayingId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingSet, setIsLoadingSet] = useState(false);
    const [hasMorePages, setHasMorePages] = useState(true);
    const [fetchVoices, setFetchVoices] = useState(false);
    const [refetchUserVoice, setRefetchUserVoice] = useState(false);
    const [voices, setVoices] = useState([]);
    const [page, setPage] = useState(1);

    const [voicesData] = useLazyGetVoicesListQuery();
    const [setVoice] = useSetUserVoiceMutation();
    const [getUserVoice, { isLoading: isLoadingUserVoice }] = useLazyGetUserVoiceQuery();

    const setAssistantVoice = async () => {
        try {
            setIsLoadingSet(true);
            await setVoice({ voice: selectedVoice }).then((apiResponse) => {
                if (apiResponse.data?.success) {
                    setIsLoadingSet(false);
                    setVisible(false);
                } else {
                    useErrorToast(apiResponse.data?.message);
                }
            });
        } catch (error) {
            console.error('Failed to set the assistant voice:', error);
        }

        setSelectedVoice(null);
    };

    const handlePlayPreview = (item) => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
        }

        const audio = new Audio(item.preview_audio);
        setCurrentAudio(audio);
        setCurrentPlayingId(item.id);
        audio.play();

        audio.onended = () => {
            setCurrentPlayingId(null);
            setCurrentAudio(null);
        };
    };

    const handlePausePreview = useCallback(() => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setCurrentPlayingId(null);
            setCurrentAudio(null);
        }
    }, [currentAudio]);

    useEffect(() => {
        if (visible) {
            setRefetchUserVoice(true);
            setFetchVoices(true);
            setVoices([]);
            setPage(1);
        }
    }, [setPage, visible]);

    useEffect(() => {
        if (refetchUserVoice) {
            getUserVoice()
                .unwrap()
                .then((res) => {
                    if (res?.response?.voice) {
                        setSelectedVoice(res.response.voice.voice);
                    }
                    setRefetchUserVoice(false);
                });
        }
    }, [getUserVoice, refetchUserVoice]);

    useEffect(() => {
        if (!visible) return;

        const timeout = setTimeout(() => {
            const el = document.querySelector('#scroll-container');
            if (!el) return;

            let processing = false;

            const handleScroll = debounce(() => {
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                    if (!processing && hasMorePages && !isLoading) {
                        processing = true;
                        setIsLoading(true);
                        setPage(page + 1);
                        setAppend(true);
                        setFetchVoices(true);

                        setTimeout(() => {
                            processing = false;
                        }, 500);
                    }
                }
            }, 100);

            el.addEventListener('scroll', handleScroll, { passive: true });

            return () => {
                el.removeEventListener('scroll', handleScroll);
            };
        }, 100);

        return () => clearTimeout(timeout);
    }, [visible, hasMorePages, isLoading, setPage, setAppend, setIsLoading, setFetchVoices, page]);

    useEffect(() => {
        if (fetchVoices) {
            setIsLoading(true);
            voicesData({ page })
                .unwrap()
                .then((res) => {
                    if (res?.response?.data) {
                        if (append) {
                            setVoices((prev) => [...prev, ...res.response.data]);
                        } else {
                            setVoices(res.response.data);
                        }
                    }
                    setHasMorePages(res.response.has_more_pages);
                    setAppend(false);
                    setFetchVoices(false);
                    setIsLoading(false);
                });
        }
    }, [append, fetchVoices, page, setIsLoading, voicesData]);

    useEffect(() => {
        if (!visible) {
            handlePausePreview();
        }
    }, [handlePausePreview, visible]);

    return (
        <Dialog
            visible={visible}
            onHide={() => setVisible(false)}
            className='rounded-4xl !w-[500px]'
            style={{ marginLeft: '15px', marginRight: '15px' }}
            breakpoints={{ '220px': '320px' }}
            contentStyle={{
                maxHeight: '50vh',
                overflowY: 'auto',
                padding: 0
            }}
            content={
                <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                    <button onClick={() => setVisible(false)} className='absolute top-6 right-6'>
                        <IconCloseFull className='fill-theme1-text5' />
                    </button>
                    <div className='text-theme1-title2 w-full mb-[10px] leading-tight'>
                        <div className={'font-bold text-center text-[22px] mb-[20px]'}>Assistant Voice</div>
                        <div id={'scroll-container'} className={'overflow-y-auto max-h-[400px] w-full space-y-3'}>
                            {(isLoading || isLoadingUserVoice) && (
                                <div className={'text-center text-theme1-primary font-bold'}> Fetching voices... </div>
                            )}
                            {!isLoading &&
                                !isLoadingUserVoice &&
                                voices.length > 0 &&
                                voices.map((item) => (
                                    <div
                                        onClick={() => setSelectedVoice(item.id)}
                                        key={item.id}
                                        className={`${selectedVoice === item.id ? 'bg-theme1-tan' : 'bg-theme1-white'} cursor-pointer justify-between flex rounded-3xl shadow-lg border border-theme1-border1 p-[16px]`}
                                    >
                                        <div className={'flex'}>
                                            <div className='w-auto mr-3'>
                                                <div className='rounded-full border-2 border-theme1-border1 w-[45px] h-[45px] overflow-hidden flex-shrink-0'>
                                                    <div className='w-full h-full bg-theme1-pale flex items-center justify-center'>
                                                        <div className='text-theme1-primary font-bold text-base'>
                                                            {item.avatar_image ? (
                                                                <img
                                                                    className={'w-[42px] h-[42px]'}
                                                                    alt=''
                                                                    src={item.avatar_image}
                                                                />
                                                            ) : (
                                                                getInitials(item.display_name)
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='grid'>
                                                <span className='font-bold text-theme1-title2'>
                                                    {item.display_name}
                                                </span>
                                                {item.preview_audio && (
                                                    <span className='font-normal text-sm text-theme1-text1'>
                                                        Audio Preview
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className='w-auto content-center'>
                                            {item.preview_audio && (
                                                <div className='flex justify-center gap-4 items-center'>
                                                    {currentPlayingId === item.id ? (
                                                        <button
                                                            className='border-none bg-none'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePausePreview();
                                                            }}
                                                            title='Pause'
                                                        >
                                                            <IconPause className='w-7 fill-theme1-primary' />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className='border-none bg-none'
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePlayPreview(item);
                                                            }}
                                                            title='Play'
                                                        >
                                                            <IconPlay className='w-7 fill-theme1-primary' />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <BaseButton
                        onClick={() => setAssistantVoice()}
                        disabled={!selectedVoice || isLoadingSet || isLoading}
                        className={'mt-5 btn-red-outline'}
                    >
                        Set Assistant Voice
                    </BaseButton>
                </div>
            }
        ></Dialog>
    );
};

export default Speechify;
